import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { getMunicipalities } from "../apis/municipalities";
import { parseMunicipalityHomeFromSearch } from "../util/municipality-context";

type MunicipalityHomeContextType = {
  codIbgeMunicipio?: number;
  municipalityName?: string;
  bannerUrl?: string | null;
  bannerLoading: boolean;
  isMunicipalityHome: boolean;
  municipioQuerySuffix: string;
};

const MunicipalityHomeContext = createContext<MunicipalityHomeContextType>({
  codIbgeMunicipio: undefined,
  municipalityName: undefined,
  bannerUrl: null,
  bannerLoading: false,
  isMunicipalityHome: false,
  municipioQuerySuffix: "",
});

export const MunicipalityHomeProvider: React.FC = ({ children }) => {
  const location = useLocation<{
    bannerUrl?: string | null;
  }>();

  const {
    codIbgeMunicipio,
    municipalityName,
    isMunicipalityHome,
    municipioQuerySuffix,
  } = useMemo(
    () => parseMunicipalityHomeFromSearch(location.search),
    [location.search]
  );

  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [bannerLoading, setBannerLoading] =
    useState<boolean>(isMunicipalityHome);

  useEffect(() => {
    if (!isMunicipalityHome || !codIbgeMunicipio || !municipalityName) {
      setBannerUrl(null);
      setBannerLoading(false);
      return;
    }
    setBannerLoading(true);
    getMunicipalities({ search: municipalityName, perPage: 10 })
      .then((response) => {
        const match = response.data.find(
          (item) => item.ibge_code === codIbgeMunicipio
        );
        const url = match?.parameters?.banner_url ?? null;
        console.log(
          `[municipality-home-provider] banner para ibge ${codIbgeMunicipio}:`,
          url
        );
        setBannerUrl(url);
      })
      .catch(() => setBannerUrl(null))
      .finally(() => setBannerLoading(false));
  }, [codIbgeMunicipio, municipalityName, isMunicipalityHome]);

  const value = useMemo(
    () => ({
      codIbgeMunicipio,
      municipalityName,
      bannerUrl,
      bannerLoading,
      isMunicipalityHome,
      municipioQuerySuffix,
    }),
    [
      codIbgeMunicipio,
      municipalityName,
      bannerUrl,
      bannerLoading,
      isMunicipalityHome,
      municipioQuerySuffix,
    ]
  );

  return (
    <MunicipalityHomeContext.Provider value={value}>
      {children}
    </MunicipalityHomeContext.Provider>
  );
};

export function useMunicipalityHome() {
  return useContext(MunicipalityHomeContext);
}
